import React from "react";
import { Loader, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface AnalysisProgressModalProps {
  isAnalyzing: boolean;
  progress: {
    current: number;
    total: number;
  };
  processedImages: string[];
}

export const AnalysisProgressModal: React.FC<AnalysisProgressModalProps> = ({
  isAnalyzing,
  progress,
  processedImages,
}) => {
  const { t } = useTranslation();

  if (!isAnalyzing) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="rounded-lg shadow-xl p-6 max-w-md w-full mx-4" style={{ backgroundColor: '#ffffff' }}>
        <div className="text-center">
          <div className="mb-4">
            <Loader className="h-12 w-12 text-primary-600 animate-spin mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("session_details.analyzing_images")}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t("session_details.processing")} {progress.current} /{" "}
            {progress.total}
          </p>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(progress.current / progress.total) * 100}%`,
              }}
            />
          </div>

          {/* Processed images list */}
          {processedImages.length > 0 && (
            <div className="mt-4 max-h-40 overflow-y-auto">
              <p className="text-xs text-gray-500 mb-2">
                {t("session_details.completed")}:
              </p>
              <div className="space-y-1">
                {processedImages.map((imageName, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 text-xs text-gray-700"
                  >
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    <span className="truncate">{imageName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
